import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { Router, X } from 'lucide-react';
import { Suspense } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { authApi } from '@/features/auth/api/auth-api';
import { AuthButton } from '@/features/auth/components/auth-button';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { useAddOrganisationManagedSuppliers } from '@/features/suppliers/api/supplier-mutations';
import { useManagedSuppliers } from '@/features/suppliers/api/supplier-queries';

const formSchema = z.object({
  suppliers: z.array(
    z.object({
      supplier: z.string().nullable(),
    }),
  ),
});

type SetupSuppliersFormValues = z.infer<typeof formSchema>;

function SetupSuppliersForm() {
  const { data: managedSuppliers } = useManagedSuppliers();
  const addOrganisationManagedSuppliers = useAddOrganisationManagedSuppliers();

  const { organisationId } = Route.useSearch();

  const navigate = Route.useNavigate();

  const form = useForm<SetupSuppliersFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      suppliers: [{ supplier: null }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: 'suppliers',
    control: form.control,
  });

  const handleSubmit = async (values: SetupSuppliersFormValues) => {
    const selectedSuppliers = values.suppliers
      .map((entry) => entry.supplier)
      .filter((id): id is string => id !== null);

    if (selectedSuppliers.length > 0) {
      await addOrganisationManagedSuppliers.mutateAsync(
        selectedSuppliers.map((id) => ({
          organisation_id: organisationId,
          supplier_id: id,
        })),
      );
    }

    // Update the custom claims with the new organisation
    await authApi.refreshSession();

    await navigate({
      to: '/$tenantId',
      params: {
        tenantId: organisationId,
      },
    });
  };

  const selectedSupplierIds = form
    .watch('suppliers')
    .map((entry) => entry.supplier);

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-semibold">Who are your suppliers?</h2>
        <p className="text-sm text-gray-600">
          Prepsheets partners with suppliers to manage your ingredients&apos;
          weights, nutrients, and calories. Let us know which suppliers you use
          to get started faster.
        </p>
      </div>
      <div className="grid gap-6">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid gap-2"
          >
            {fields.map((field, index) => (
              <FormField
                key={field.id}
                control={form.control}
                name={`suppliers.${index}.supplier`}
                render={({ field }) => (
                  <FormItem className="flex items-center gap-4">
                    <div className="flex-1">
                      <FormLabel>Supplier</FormLabel>
                      <div className="flex">
                        <Select
                          onValueChange={field.onChange}
                          value={String(field.value)}
                        >
                          <FormControl>
                            <SelectTrigger className="h-12">
                              <SelectValue placeholder="Select a supplier" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {managedSuppliers
                              .filter(
                                (supplier) =>
                                  !selectedSupplierIds.includes(supplier.id) ||
                                  supplier.id === field.value,
                              )
                              .map((supplier) => (
                                <SelectItem
                                  key={supplier.id}
                                  value={String(supplier.id)}
                                >
                                  {supplier.title}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="p-2 rounded hover:bg-muted ml-2"
                          aria-label={`Remove supplier ${index + 1}`}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            ))}
            <AuthButton
              type="button"
              onClick={() => append({ supplier: null })}
              disabled={managedSuppliers.length === fields.length}
            >
              Add Supplier
            </AuthButton>
            <div className="flex justify-between items-center mt-4 gap-4">
              {/* <AuthButton onClick={onBack} type="button">
                Back
              </AuthButton> */}
              <AuthButton type="submit">Submit</AuthButton>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

function SetupSuppliersLoading() {
  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <Skeleton className="h-8 w-3/4 mx-auto" />
        <Skeleton className="h-16 w-full mt-2" />
      </div>
      <div className="grid gap-6">
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-32" />
          <div className="flex justify-between items-center mt-4 gap-4">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>
      </div>
    </div>
  );
}

function SetupSuppliersPage() {
  return (
    <AuthLayout>
      <Suspense fallback={<SetupSuppliersLoading />}>
        <SetupSuppliersForm />
      </Suspense>
    </AuthLayout>
  );
}

const setupSuppliersSearchSchema = z.object({
  organisationId: z.string(),
});

export const Route = createFileRoute('/_authenticated/onboard/setup-suppliers')(
  {
    validateSearch: setupSuppliersSearchSchema,
    beforeLoad: ({ search }) => {
      if (!search.organisationId) {
        throw redirect({ to: '/' });
      }
    },
    component: SetupSuppliersPage,
  },
);
